import { storage } from '@/libs/storage/mmkv';

/**
 * Client-side throttle for verification emails (PP-330).
 *
 * Firebase rate-limits `sendEmailVerification` server-side and returns
 * `auth/too-many-requests` once exceeded. To avoid hitting that limit when a
 * user repeatedly logs in with an unverified account, we remember the last time
 * a verification email was sent (per email) and only re-trigger Firebase once
 * the cooldown has elapsed. Otherwise we just route the user to the
 * "Verify Email" screen without sending a new email.
 */
export const VERIFY_EMAIL_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour

/** MMKV key holding the last-sent timestamp (epoch ms) for a given email. */
const keyFor = (email: string) => `verify-email-last-sent:${email.trim().toLowerCase()}`;

/** Returns the last time (epoch ms) a verification email was sent to `email`, or null. */
export const getVerificationSentAt = (email: string): number | null => {
  if (!email) return null;
  // Stored as a string so the web localStorage fallback (string-only) works too.
  const value = storage.getString(keyFor(email));
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Records that a verification email was just sent to `email`. */
export const setVerificationSentAt = (email: string, sentAt: number): void => {
  if (!email) return;
  storage.set(keyFor(email), String(sentAt));
};

/**
 * Whether a new verification email should be sent for `email`.
 *
 * @param now Current time (epoch ms). Injected so callers/tests stay deterministic.
 * @returns true when no email was sent yet or the cooldown has elapsed.
 */
export const shouldResendVerification = (email: string, now: number): boolean => {
  const lastSentAt = getVerificationSentAt(email);
  if (lastSentAt == null) return true;
  return now - lastSentAt >= VERIFY_EMAIL_COOLDOWN_MS;
};
